import { useCallback } from 'react';

import type { NotificationPayload } from 'newui/hooks/useUserNotifications';
import { useUserNotifications } from 'newui/hooks/useUserNotifications';

export interface UseRevisionNotificationsProps {
    rfxInviteId: number | undefined;
    userId: number | undefined;
    onRevisionApproved?: () => void;
    onRevisionDeclined?: () => void;
    onRevisionRequested?: () => void;
    onRevisionSubmitted?: () => void;
}

const NOTIFICATION_TYPES = {
    REVISION_REQUESTED: 'revision_requested',
    REVISION_APPROVED: 'revision_approved',
    REVISION_DECLINED: 'revision_declined',
    REVISION_SUBMITTED: 'revision_submitted',
} as const;

const useRevisionNotifications = ({
    userId,
    rfxInviteId,
    onRevisionRequested,
    onRevisionApproved,
    onRevisionDeclined,
    onRevisionSubmitted,
}: UseRevisionNotificationsProps): void => {
    const handleNotification = useCallback(
        (payload: NotificationPayload) => {
            if (rfxInviteId === undefined) {
                return;
            }

            if (payload.rfx_invite_id !== rfxInviteId) {
                return;
            }

            switch (payload.type) {
                case NOTIFICATION_TYPES.REVISION_REQUESTED:
                    onRevisionRequested?.();
                    break;
                case NOTIFICATION_TYPES.REVISION_APPROVED:
                    onRevisionApproved?.();
                    break;
                case NOTIFICATION_TYPES.REVISION_DECLINED:
                    onRevisionDeclined?.();
                    break;
                case NOTIFICATION_TYPES.REVISION_SUBMITTED:
                    onRevisionSubmitted?.();
                    break;
                default:
                    break;
            }
        },
        [
            rfxInviteId,
            onRevisionRequested,
            onRevisionApproved,
            onRevisionDeclined,
            onRevisionSubmitted,
        ]
    );

    useUserNotifications(userId, handleNotification);
};

export { useRevisionNotifications };
